import { useState, useEffect, useReducer, useCallback } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { api } from '../../../lib/apiClient';
import { settingsReducer, initialSettings } from './settingsReducer';
import { waSessionReducer, initialWaState } from './waSessionReducer';
import { webhookFormReducer, makeInitialWebhookForm } from './webhookFormReducer';
import { recipientsReducer, initialRecipientsState } from './recipientsReducer';
import { filtersReducer, initialFiltersState } from './filtersReducer';

// Helper to generate UUID
const uuidv4 = () => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
        const r = Math.random() * 16 | 0, v = c == 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
    });
};

// All state, effects, and handlers for NotificationConfigView. Kept in one hook so
// every handler/effect closure stays co-located (identical behavior to the previous
// in-component definitions) while the component itself stays presentational.
export function useNotificationConfig(integration, onBack) {
    const { user } = useAuth();

    // Discord webhook master-detail form cluster (view mode, edited id, name, url).
    const [webhookForm, dispatchWebhookForm] = useReducer(webhookFormReducer, integration.id, makeInitialWebhookForm);
    const { viewMode, editingWebhookId, name: webhookName, url: webhookUrl } = webhookForm;
    const setViewMode = (value) => dispatchWebhookForm({ type: 'SET_FIELD', field: 'viewMode', value });
    const setWebhookName = (value) => dispatchWebhookForm({ type: 'SET_FIELD', field: 'name', value });
    const setWebhookUrl = (value) => dispatchWebhookForm({ type: 'SET_FIELD', field: 'url', value });

    const [webhooks, setWebhooks] = useState([]); // Array of webhook objects

    // Form State (Single Source of Truth, reused)
    const [permissionStatus, setPermissionStatus] = useState('default'); // For Push

    // WhatsApp pairing-session cluster (session blob, loading flags, groups list).
    const [wa, dispatchWa] = useReducer(waSessionReducer, initialWaState);
    const { session: waSession, sessionLoading: waSessionLoading, groups: waGroups, groupsRefreshing: waGroupsRefreshing } = wa;
    const setWaSession = (value) => dispatchWa({ type: 'SET_FIELD', field: 'session', value });
    const setWaSessionLoading = (value) => dispatchWa({ type: 'SET_FIELD', field: 'sessionLoading', value });
    const setWaGroups = (value) => dispatchWa({ type: 'SET_FIELD', field: 'groups', value });
    const setWaGroupsRefreshing = (value) => dispatchWa({ type: 'SET_FIELD', field: 'groupsRefreshing', value });

    // WhatsApp recipients (phones + groups) + picker search.
    const [recipientsState, dispatchRecipients] = useReducer(recipientsReducer, initialRecipientsState);
    const { recipients, search: recipientSearch } = recipientsState;
    const setRecipients = (value) => dispatchRecipients({ type: 'SET_RECIPIENTS', value });
    const setRecipientSearch = (value) => dispatchRecipients({ type: 'SET_SEARCH', value });

    // Saved-filters cluster (list, enabled map, open config modal id).
    const [filtersState, dispatchFilters] = useReducer(filtersReducer, initialFiltersState);
    const { savedFilters, enabledFilters, activeConfigFilter } = filtersState;
    const setSavedFilters = (value) => dispatchFilters({ type: 'SET_SAVED_FILTERS', value });
    const setEnabledFilters = (value) => dispatchFilters({ type: 'SET_ENABLED_FILTERS', value });
    const setActiveConfigFilter = (value) => dispatchFilters({ type: 'SET_ACTIVE_CONFIG_FILTER', value });

    // Consolidated "settings form" cluster: notificationType, globalFrequency,
    // quietHoursEnabled, quietHoursSlots, filterConfigs. These five always move as
    // a unit (reset / hydrate / save), so they live in one reducer.
    const [settings, dispatchSettings] = useReducer(settingsReducer, initialSettings);
    const { notificationType, globalFrequency, quietHoursEnabled, quietHoursSlots, filterConfigs } = settings;

    // Thin setter wrappers preserving the original useState call signatures so
    // every update site keeps identical semantics.
    const setNotificationType = (value) => dispatchSettings({ type: 'SET_FIELD', field: 'notificationType', value });
    const setGlobalFrequency = (value) => dispatchSettings({ type: 'SET_FIELD', field: 'globalFrequency', value });
    const setQuietHoursEnabled = (value) => dispatchSettings({ type: 'SET_FIELD', field: 'quietHoursEnabled', value });
    const setQuietHoursSlots = (updater) => dispatchSettings({ type: 'UPDATE_FIELD', field: 'quietHoursSlots', updater });
    const setFilterConfigs = (updater) => dispatchSettings({ type: 'UPDATE_FIELD', field: 'filterConfigs', updater });

    // Global Slot Handlers
    const addTimeSlot = () => setQuietHoursSlots(prev => [...prev, { id: Date.now(), from: '22:00', to: '08:00' }]);
    const removeTimeSlot = (id) => setQuietHoursSlots(prev => prev.filter(slot => slot.id !== id));
    const updateTimeSlot = (id, field, value) => {
        setQuietHoursSlots(prev => prev.map(slot => slot.id === id ? { ...slot, [field]: value } : slot));
    };

    // Filter Slot Handlers & Helpers
    const getFilterConfig = (filterId) => filterConfigs[filterId] || {
        frequency: 'inherit',
        useGlobalQuietHours: true,
        quietHoursEnabled: false,
        slots: [{ id: Date.now(), from: '22:00', to: '08:00' }]
    };

    const updateFilterConfig = (filterId, updates) => {
        setFilterConfigs(prev => ({
            ...prev,
            [filterId]: { ...getFilterConfig(filterId), ...updates }
        }));
    };

    const addFilterSlot = (filterId) => {
        const config = getFilterConfig(filterId);
        updateFilterConfig(filterId, {
            slots: [...config.slots, { id: Date.now(), from: '22:00', to: '08:00' }]
        });
    };

    const removeFilterSlot = (filterId, slotId) => {
        const config = getFilterConfig(filterId);
        updateFilterConfig(filterId, {
            slots: config.slots.filter(s => s.id !== slotId)
        });
    };

    const updateFilterSlot = (filterId, slotId, field, value) => {
        const config = getFilterConfig(filterId);
        updateFilterConfig(filterId, {
            slots: config.slots.map(s => s.id === slotId ? { ...s, [field]: value } : s)
        });
    };

    // Check Permission (for Push)
    useEffect(() => {
        if (integration.id === 'push' && 'Notification' in window) {
            setPermissionStatus(Notification.permission);
        }
    }, [integration.id]);

    // Load Data
    useEffect(() => {
        if (!user || !integration?.id) return;

        const fetchData = async () => {
            try {
                const settingsData = await api.getNotificationSettings();
                const settings = settingsData.settings?.[integration.id] || {};

                if (integration.id === 'discord') {
                    const allWebhooks = settings.webhooks || [];
                    const uniqueWebhooks = Array.from(new Map(allWebhooks.map(w => [w.id, w])).values());
                    setWebhooks(uniqueWebhooks);
                } else {
                    const loaded = {};
                    if (settings.notificationType) loaded.notificationType = settings.notificationType;
                    if (settings.globalFrequency) loaded.globalFrequency = settings.globalFrequency;
                    if (settings.quietHoursEnabled !== undefined) loaded.quietHoursEnabled = settings.quietHoursEnabled;
                    if (settings.quietHoursSlots) loaded.quietHoursSlots = settings.quietHoursSlots;
                    if (settings.filterConfigs) loaded.filterConfigs = settings.filterConfigs;
                    if (Object.keys(loaded).length) dispatchSettings({ type: 'LOAD', values: loaded });
                    // enabledFilters is derived from saved_filters.notifications below —
                    // the JSON map in settings is no longer used as a gate.
                    if (integration.id === 'whatsapp') {
                        if (Array.isArray(settings.recipients) && settings.recipients.length > 0) {
                            setRecipients(settings.recipients);
                        } else if (settings.phoneNumber) {
                            setRecipients([{ type: 'phone', value: settings.phoneNumber, label: settings.phoneNumber }]);
                        }
                    }
                }

                const filtersData = await api.getFilters();
                const filters = filtersData.filters || [];
                setSavedFilters(filters);
                // Single source of truth: derive the toggle UI state from each
                // filter's `notifications` column. No drift vs. the dispatcher.
                setEnabledFilters(Object.fromEntries(filters.map((f) => [f.id, f.notifications === 1 || f.notifications === true])));
            } catch (err) {
                console.error('Error loading notification data:', err);
            }
        };
        fetchData();
    }, [user, integration.id]);

    // WhatsApp session polling (Baileys QR + connection status)
    useEffect(() => {
        if (integration.id !== 'whatsapp') return;
        let cancelled = false;
        const tick = async () => {
            if (cancelled) return;
            try {
                setWaSessionLoading(true);
                const data = await api.getWhatsAppSession();
                if (!cancelled) setWaSession(data);
            } catch (err) {
                console.error('[wa-session] poll failed:', err);
            } finally {
                if (!cancelled) setWaSessionLoading(false);
            }
        };
        tick();
        const id = setInterval(tick, 1500);
        return () => { cancelled = true; clearInterval(id); };
    }, [integration.id]);

    // WhatsApp groups list (refreshed when session flips state, plus a slow background poll)
    const fetchWaGroups = async () => {
        try {
            setWaGroupsRefreshing(true);
            const data = await api.getWhatsAppGroups();
            setWaGroups(data.groups || []);
        } catch (err) {
            console.error('[wa-groups] load failed:', err);
        } finally {
            setWaGroupsRefreshing(false);
        }
    };

    useEffect(() => {
        if (integration.id !== 'whatsapp') return;
        fetchWaGroups();
        // Race: bridge posts status=connected before it has time to shell out
        // to `wacli groups list` and post the result. Aggressively refetch for
        // a few seconds after a transition, then settle into a slow poll.
        const burst = waSession?.status === 'connected'
            ? [800, 2000, 4000, 7000].map((d) => setTimeout(fetchWaGroups, d))
            : [];
        const id = setInterval(fetchWaGroups, 10000);
        return () => {
            clearInterval(id);
            burst.forEach(clearTimeout);
        };
    }, [integration.id, waSession?.status]);


    // Handlers for List Actions
    const handleAddWebhook = () => {
        dispatchWebhookForm({ type: 'MERGE', values: { editingWebhookId: 'new', name: '', url: '', viewMode: 'edit' } });
        // Reset Settings to Defaults
        dispatchSettings({ type: 'RESET' });
        setEnabledFilters({});
    };

    const handleEditWebhook = (webhook) => {
        dispatchWebhookForm({ type: 'MERGE', values: { editingWebhookId: webhook.id, name: webhook.name, url: webhook.url, viewMode: 'edit' } });
        // Load Specific Webhook Settings
        const s = webhook.settings || {};
        dispatchSettings({
            type: 'LOAD',
            values: {
                notificationType: s.notificationType || 'summary',
                globalFrequency: s.globalFrequency || '10m',
                quietHoursEnabled: s.quietHoursEnabled || false,
                quietHoursSlots: s.quietHoursSlots || [{ id: 1, from: '22:00', to: '08:00' }],
                filterConfigs: s.filterConfigs || {},
            },
        });
        setEnabledFilters(s.enabledFilters || {});
    };

    const handleDeleteWebhook = async (id) => {
        if (!confirm("Are you sure you want to delete this webhook?")) return;
        const newWebhooks = webhooks.filter(w => w.id !== id);
        setWebhooks(newWebhooks);
        // Force save immediately
        await saveSettingsToDB(newWebhooks);
    };

    // Auto-Save Logic
    const saveSettingsToDB = useCallback(async (currentWebhooks) => {
        if (!user || !integration?.id) return;

        let settingsToSave = {};

        if (integration.id === 'discord') {
            settingsToSave = { webhooks: currentWebhooks };
        } else {
            // Push / WhatsApp Integration (Single)
            settingsToSave = {
                notificationType,
                globalFrequency,
                quietHoursEnabled,
                quietHoursSlots,
                filterConfigs,
                enabledFilters
            };
            if (integration.id === 'whatsapp') {
                settingsToSave.recipients = recipients;
                // legacy mirror so older clients still work
                const firstPhone = recipients.find((r) => r.type === 'phone');
                if (firstPhone) settingsToSave.phoneNumber = firstPhone.value;
            }
        }

        try {
            await api.updateNotificationSettings(integration.id, settingsToSave);
        } catch (err) {
            console.error('Error saving notification settings:', err);
        }
    }, [user, integration, notificationType, globalFrequency, quietHoursEnabled, quietHoursSlots, filterConfigs, enabledFilters, recipients]);

    // Debounced Save for Form Changes
    useEffect(() => {
        if (!user || !integration?.id) return;
        // Don't auto-save if simply viewing list
        if (integration.id === 'discord' && viewMode === 'list') return;

        // If editing a webhook, we need to update the `webhooks` array in memory first.
        // We do NOT auto-save to DB while typing in Edit Form to avoid chaos.
        // We will save on "Save" button or navigating back.
        if (integration.id === 'discord' && viewMode === 'edit') {
            return;
        }

        // For Push (Single Mode), Auto-Save is fine
        if (integration.id !== 'discord') {
            const saveTimer = setTimeout(() => {
                saveSettingsToDB(null); // Pass null to trigger "Push" logic branch
            }, 1000);
            return () => clearTimeout(saveTimer);
        }

    }, [user, integration.id, viewMode, saveSettingsToDB]);


    // Save Handler for Discord Webhook Form
    const handleSaveDiscordForm = async () => {
        if (!webhookUrl) return alert("Webhook URL is required.");
        if (!webhookName) return alert("Webhook Name is required.");

        const currentSettings = {
            notificationType,
            globalFrequency,
            quietHoursEnabled,
            quietHoursSlots,
            filterConfigs,
            enabledFilters
        };

        let updatedWebhooks = [...webhooks];
        if (editingWebhookId === 'new') {
            updatedWebhooks.push({
                id: uuidv4(),
                user_id: user.id,
                name: webhookName || 'New Webhook',
                url: webhookUrl,
                settings: currentSettings
            });
        } else {
            updatedWebhooks = updatedWebhooks.map(w => w.id === editingWebhookId ? {
                ...w,
                user_id: user.id,
                name: webhookName,
                url: webhookUrl,
                settings: currentSettings
            } : w);
        }

        setWebhooks(updatedWebhooks);
        await saveSettingsToDB(updatedWebhooks);


        await saveSettingsToDB(updatedWebhooks);
        // Sync handled inside saveSettingsToDB now
        setViewMode('list');
    };

    // ... (Permission Logic for Push) ...
    const requestPermission = async () => {
        if (!('Notification' in window)) {
            alert("This browser does not support desktop notifications");
            return;
        }

        if (Notification.permission === 'denied') {
            alert("Notifications are blocked. Please enable them in your browser settings for this site.");
            return;
        }

        const permission = await Notification.requestPermission();
        setPermissionStatus(permission);
    };

    // ... (Helper Functions) ...
    const toggleFilter = async (filterId) => {
        const newValue = !enabledFilters[filterId];
        setEnabledFilters(prev => ({ ...prev, [filterId]: newValue }));
        try {
            await api.updateFilter(filterId, { notifications: newValue });
        } catch (err) {
            console.error('Failed to persist filter notifications toggle:', err);
            setEnabledFilters(prev => ({ ...prev, [filterId]: !newValue }));
            alert(`Failed to update notifications: ${err.message}`);
        }
    };

    const sendTestNotification = async () => {
        if (integration.id === 'discord') {
            if (!webhookUrl) return alert("Please enter a Discord Webhook URL first.");
            try {
                const payload = {
                    content: "🔔 **Test Notification**\nThis is a test message from your Mekko Job Notifier dashboard!"
                };

                await fetch(webhookUrl, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload)
                });
                alert("Test sent! Check your Discord channel.");
            } catch (err) {
                console.error("Discord Webhook Error:", err);
                alert("Failed to send test. Check console and CORS policy.");
            }
            return;
        }
        if (integration.id === 'push') {
            if (Notification.permission === 'granted') {
                new Notification("Test Notification", { body: "This is a test from Mekko Job Notifier!" });
            } else {
                requestPermission();
            }
        }
        if (integration.id === 'whatsapp') {
            if (!recipients.length) {
                alert("Pick at least one recipient (your number or a WhatsApp group) above.");
                return;
            }
            try {
                const res = await api.sendWhatsAppTest({ recipients });
                alert(`Queued ${res.count || recipients.length} test message${(res.count || recipients.length) > 1 ? 's' : ''}. WhatsApp will deliver within a few seconds if your sender is connected.`);
            } catch (err) {
                console.error('WhatsApp test enqueue failed:', err);
                alert(`Failed to queue test: ${err.message}`);
            }
        }
    };

    const headerTitle = integration.id === 'discord'
        ? (viewMode === 'list' ? 'Discord Webhooks' : (editingWebhookId === 'new' ? 'Add Webhook' : webhookName || 'Edit Webhook'))
        : integration.label;

    const handleHeaderBack = () => {
        if (viewMode === 'edit' && integration.id === 'discord') {
            setViewMode('list'); // Go back to List
        } else {
            onBack(); // Go back to Interactions Menu
        }
    };

    return {
        // webhook form
        viewMode, editingWebhookId, webhookName, webhookUrl, setWebhookName, setWebhookUrl,
        webhooks,
        // push
        permissionStatus, requestPermission,
        // whatsapp session + groups
        waSession, waSessionLoading, waGroups, waGroupsRefreshing, setWaSession, setWaGroups, fetchWaGroups,
        // recipients
        recipients, recipientSearch, setRecipients, setRecipientSearch,
        // filters
        savedFilters, enabledFilters, activeConfigFilter, setActiveConfigFilter, toggleFilter,
        // settings form
        notificationType, setNotificationType,
        globalFrequency, setGlobalFrequency,
        quietHoursEnabled, setQuietHoursEnabled,
        quietHoursSlots, addTimeSlot, removeTimeSlot, updateTimeSlot,
        filterConfigs, getFilterConfig, updateFilterConfig, addFilterSlot, removeFilterSlot, updateFilterSlot,
        // handlers
        handleAddWebhook, handleEditWebhook, handleDeleteWebhook, handleSaveDiscordForm, sendTestNotification,
        // header
        headerTitle, handleHeaderBack,
    };
}
